import pino, {
  Bindings,
  ChildLoggerOptions,
  Logger as PinoLogger,
  LogFn,
} from 'pino';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDAMApp } from './services/firebase';

const level = process.env.NODE_ENV === 'prod' ? 'info' : 'trace';

/** Keep untrusted values on one physical line so they cannot forge log entries. */
export const sanitizeForLog = (value: unknown): string =>
  String(value)
    .replace(/\u2028|\u2029/g, '')
    .replace(/\n|\r/g, '');

export interface SanitizingLogger {
  trace: LogFn;
  debug: LogFn;
  info: LogFn;
  warn: LogFn;
  error: LogFn;
  fatal: LogFn;
  child(bindings: Bindings, options?: ChildLoggerOptions): SanitizingLogger;
}

const sanitizeLogArguments = (args: any[]): any[] =>
  args.map((arg) => (typeof arg === 'string' ? sanitizeForLog(arg) : arg));

const wrapLogMethod = (delegate: PinoLogger, method: LogFn): LogFn =>
  ((...args: any[]) => {
    (method as (...safeArgs: any[]) => void).apply(
      delegate,
      sanitizeLogArguments(args)
    );
  }) as LogFn;

/**
 * Keep the raw Pino object private: CodeQL models direct Pino calls as sinks,
 * while caller-supplied message strings pass through the sanitizer here.
 */
export const createSanitizingLogger = (
  delegate: PinoLogger,
  messageKey = 'msg'
): SanitizingLogger => ({
  trace: wrapLogMethod(delegate, delegate.trace),
  debug: wrapLogMethod(delegate, delegate.debug),
  info: wrapLogMethod(delegate, delegate.info),
  warn: wrapLogMethod(delegate, delegate.warn),
  error: wrapLogMethod(delegate, delegate.error),
  fatal: wrapLogMethod(delegate, delegate.fatal),
  child(bindings, options) {
    const childBindings = bindings.serializers
      ? {
          ...bindings,
          serializers: {
            ...bindings.serializers,
            [messageKey]: sanitizeForLog,
          },
        }
      : bindings;
    const childOptions = {
      ...options,
      serializers: {
        ...options?.serializers,
        [messageKey]: sanitizeForLog,
      },
    };

    return createSanitizingLogger(
      delegate.child(childBindings, childOptions),
      messageKey
    );
  },
});

const pinoLogger = pino({
  level,
  name: 'digital-accounts-management',
  serializers: { msg: sanitizeForLog },
});

export const logger = createSanitizingLogger(pinoLogger);

export const serverLogger = async (message: string, level: string) => {
  try {
    const damApp = getDAMApp();
    const functions = getFunctions(damApp);
    const callable = httpsCallable(functions, 'accountManager-callableLogger');
    await callable({ message, level });
  } catch (e) {
    logger.error(`Error calling callableLogger, e: ${e}`);
    throw new Error('Error calling callableLogger');
  }
};
